import React, { useEffect, useMemo, useState } from "react";
import {
  CalendarClock,
  CalendarDays,
  CalendarPlus,
  Check,
  Clock,
  Globe2,
  Info,
  Plus,
  Repeat,
  Rocket,
  Sparkles,
  Trash2,
  Zap,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { QuizQuestion } from "./QuizShell";
import { OptionCard } from "./OptionCard";
import { FieldLabel } from "./shared";
import type { QuizProgress } from "./types";

type RepeatKind = "none" | "daily" | "weekly" | "monthly" | "custom";
type Screen = "when" | "repeat" | "range" | "dates" | "confirm";

export interface ScheduleQuizProps {
  progress: QuizProgress;
  onTotal: (n: number) => void;

  // timing
  scheduleOption: "now" | "later";
  selectedDateTime: string;
  setSelectedDateTime: (v: string) => void;
  onChooseNow: () => void;
  onChooseLater: () => void;
  /** Picks one of the Auto IQ recommended times (switches to "later"). */
  onPickRecommended: (inputValue: string) => void;
  /** Auto IQ send-time suggestions (same data as the classic step). */
  bestTimes: any[];
  analysisFactors: string[];
  effectiveTimezone: string;
  /** Formats an ISO / local date string for display in the user's timezone. */
  formatDisplay: (value: string) => string;
  /** Formats "now" with seconds for the live clock card. */
  formatNow: () => string;
  /** min value for datetime-local inputs, in the user's timezone. */
  minDateTime: string;
  localToUtcIso: (value: string) => string | null;
  onChangeTimezone: () => void;

  // recurring
  isRecurringCampaign: boolean;
  recurrenceType: "daily" | "weekly" | "monthly" | "custom";
  setRecurrenceType: (v: "daily" | "weekly" | "monthly" | "custom") => void;
  recurringStartDateTime: string;
  setRecurringStartDateTime: (v: string) => void;
  recurringEndDate: string;
  setRecurringEndDate: (v: string) => void;
  customRecurringDates: string[];
  setCustomRecurringDates: (v: string[]) => void;
  sortDates: (d: string[]) => string[];
  recurringCount: number | null;
  /** Builds the recurring plan from the current inputs (null data = invalid). */
  buildRecurring: () => { error: string | null; data: unknown };
  applyRecurring: () => void;
  removeRecurring: () => void;
  /** The classic flow asks the quiz to re-open its recurring editor. */
  showRecurringModal: boolean;
  setShowRecurringModal: (v: boolean) => void;

  // confirm
  acceptedTerms: boolean;
  setAcceptedTerms: (v: boolean) => void;
  canContinue: boolean;
  continueLoading?: boolean;
  onContinue: () => void;
  onBack: () => void;
  /** Screen to open first (defaults to the "when" question). */
  initialScreen?: "when" | "repeat" | "range" | "dates" | "confirm";
}

const REPEAT_LABEL: Record<RepeatKind, string> = {
  none: "Just once",
  daily: "Every day",
  weekly: "Every week",
  monthly: "Every month",
  custom: "Custom dates",
};

const dtInputClass =
  "h-12 w-full rounded-xl border border-slate-300 bg-white px-4 text-base text-slate-900 shadow-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100";

const openPicker = (e: React.MouseEvent<HTMLInputElement>) => {
  const t = e.currentTarget as HTMLInputElement;
  if (typeof t.showPicker === "function") {
    try {
      t.showPicker();
    } catch {
      /* some browsers only allow it on user gesture */
    }
  }
};

export const ScheduleQuiz: React.FC<ScheduleQuizProps> = (p) => {
  const { toast } = useToast();
  const [screen, setScreen] = useState<Screen>(p.initialScreen || "when");
  const [repeat, setRepeat] = useState<RepeatKind>(
    p.isRecurringCampaign ? p.recurrenceType : "none"
  );
  const [customDraft, setCustomDraft] = useState("");
  const [, setTick] = useState(0);

  // keeps the "current time" card fresh
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 30000);
    return () => clearInterval(t);
  }, []);

  // The classic flow (review dialog "Back", trial modal "No, go back", missing
  // recurring dates…) asks to re-open the recurring editor: show our screen.
  useEffect(() => {
    if (!p.showRecurringModal) return;
    p.setShowRecurringModal(false);
    setScreen("repeat");
  }, [p.showRecurringModal]); // eslint-disable-line react-hooks/exhaustive-deps

  const needsDetail = repeat !== "none";
  const flow: Screen[] = useMemo(
    () => [
      "when",
      "repeat",
      ...(repeat === "custom" ? (["dates"] as Screen[]) : needsDetail ? (["range"] as Screen[]) : []),
      "confirm",
    ],
    [repeat, needsDetail]
  );
  const idx = Math.max(0, flow.indexOf(screen));
  const position = p.progress.before + idx + 1;
  const total = p.progress.before + flow.length + p.progress.after;

  useEffect(() => {
    p.onTotal(flow.length);
  }, [flow.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const q = { position, total } as const;
  const goNext = () => setScreen(flow[Math.min(flow.length - 1, idx + 1)]);
  const goBack = () => (idx === 0 ? p.onBack() : setScreen(flow[idx - 1]));

  const tzNote = (
    <div className="mb-6 rounded-2xl border border-blue-200 bg-blue-50/70 p-4">
      <div className="flex flex-wrap items-start gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-600">
          <Globe2 className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-blue-900">
            Times are shown in {p.effectiveTimezone}
          </p>
          <p className="text-sm text-blue-800">Now: {p.formatNow()}</p>
          <button
            type="button"
            onClick={p.onChangeTimezone}
            className="mt-1 text-xs font-medium text-blue-700 underline underline-offset-2"
          >
            Change timezone in Account Settings
          </button>
        </div>
      </div>
    </div>
  );

  const validatePast = (value: string, label: string): boolean => {
    const utc = p.localToUtcIso(value) || value;
    if (!utc || new Date(utc) < new Date(Date.now() - 30 * 1000)) {
      toast({
        title: "Invalid Date",
        description: label,
        variant: "destructive",
        duration: 1500,
      });
      return false;
    }
    return true;
  };

  /* ───────────────── 1. When ───────────────── */
  if (screen === "when") {
    const recs = p.bestTimes.slice(0, 3);
    const matchesAi = recs.some((r) => r.inputValue === p.selectedDateTime);
    const customSelected = p.scheduleOption === "later" && !matchesAi;

    return (
      <QuizQuestion
        {...q}
        title="When should your launch go out?"
        hint="Pick a recommended time, send it right away, or choose your own."
        onBack={goBack}
        onContinue={goNext}
        continueDisabled={p.scheduleOption === "later" && !p.selectedDateTime}
        size="lg"
      >
        {tzNote}

        {recs.length > 0 && (
          <div className="mb-6 rounded-2xl border border-indigo-200 bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-5 shadow-sm">
            <div className="mb-4 flex items-start gap-3">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 text-white shadow">
                <Sparkles className="h-5 w-5" />
              </span>
              <div>
                <p className="text-base font-bold text-slate-900">
                  Send-time recommendations from Auto IQ
                </p>
                <p className="text-sm text-slate-600">
                  Calculated from your data in {p.effectiveTimezone}
                </p>
              </div>
            </div>
            {p.analysisFactors.length > 0 && (
              <div className="mb-4 flex flex-wrap gap-2">
                {p.analysisFactors.map((f, i) => (
                  <Badge key={i} variant="secondary" className="bg-white/80 text-xs">
                    {f}
                  </Badge>
                ))}
              </div>
            )}
            <div className="space-y-3">
              {recs.map((t, i) => {
                const selected =
                  p.scheduleOption === "later" && p.selectedDateTime === t.inputValue;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => p.onPickRecommended(t.inputValue)}
                    className={cn(
                      "relative w-full rounded-xl border-2 bg-white p-4 text-left transition",
                      selected
                        ? "border-blue-500 ring-2 ring-blue-200 shadow-md"
                        : i === 0
                          ? "border-green-300 hover:border-green-400 hover:shadow-md"
                          : "border-slate-200 hover:border-blue-300 hover:shadow-md"
                    )}
                  >
                    {selected && (
                      <span className="absolute right-3 top-3 flex h-6 w-6 items-center justify-center rounded-full bg-blue-600 text-white">
                        <Check className="h-4 w-4" />
                      </span>
                    )}
                    <div className="flex flex-wrap items-center gap-2 pr-8">
                      <span className="text-base font-semibold text-slate-900">
                        {t.dayName} at {t.formattedTime}
                      </span>
                      {i === 0 && (
                        <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
                          Best match
                        </Badge>
                      )}
                      {t.category && (
                        <Badge variant="outline" className="text-xs">
                          {t.category}
                        </Badge>
                      )}
                    </div>
                    {t.reason && (
                      <p className="mt-1 line-clamp-2 text-sm text-slate-600">{t.reason}</p>
                    )}
                    <p className="mt-2 text-xs text-slate-500">
                      📅 {t.formattedDate} · 🕐 {p.effectiveTimezone}
                    </p>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <p className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-400">
          Or choose your own
        </p>
        <div className="grid gap-4 sm:grid-cols-2">
          <OptionCard
            layout="row"
            icon={Zap}
            tone="emerald"
            title="Send now"
            description="Goes out within about a minute."
            selected={p.scheduleOption === "now"}
            onClick={p.onChooseNow}
          />
          <OptionCard
            layout="row"
            icon={CalendarClock}
            tone="blue"
            title="Pick a date & time"
            description="Schedule it for later."
            selected={customSelected || (p.scheduleOption === "later" && !p.selectedDateTime)}
            onClick={p.onChooseLater}
          />
        </div>

        {p.scheduleOption === "later" && (
          <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <FieldLabel required>Select date &amp; time ({p.effectiveTimezone})</FieldLabel>
            <input
              type="datetime-local"
              value={p.selectedDateTime}
              min={p.minDateTime || undefined}
              onClick={openPicker}
              onChange={(e) => {
                const v = e.target.value;
                if (!v) {
                  p.setSelectedDateTime("");
                  return;
                }
                if (!p.localToUtcIso(v)) {
                  toast({
                    title: "Invalid Date",
                    description: "Please select a valid date and time.",
                    variant: "destructive",
                    duration: 1500,
                  });
                  p.setSelectedDateTime("");
                  return;
                }
                if (!validatePast(v, "You cannot schedule a launch in the past.")) {
                  p.setSelectedDateTime("");
                  return;
                }
                p.setSelectedDateTime(v);
              }}
              className={dtInputClass}
            />
            <p className="mt-2 text-xs text-slate-500">
              We'll save this in UTC. Please pick a time at least 2 minutes from now.
            </p>
          </div>
        )}
      </QuizQuestion>
    );
  }

  /* ───────────────── 2. Repeat? ───────────────── */
  if (screen === "repeat") {
    const choose = (r: RepeatKind) => {
      setRepeat(r);
      if (r === "none") {
        if (p.isRecurringCampaign) p.removeRecurring();
        setScreen("confirm");
        return;
      }
      p.setRecurrenceType(r);
      setScreen(r === "custom" ? "dates" : "range");
    };
    return (
      <QuizQuestion
        {...q}
        title="Should this launch repeat?"
        hint="Send it once, or set it to repeat automatically."
        onBack={goBack}
        size="lg"
      >
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <OptionCard
            icon={Rocket}
            tone="slate"
            title="Just once"
            description="A single send."
            selected={repeat === "none"}
            onClick={() => choose("none")}
          />
          <OptionCard
            icon={Repeat}
            tone="blue"
            title="Every day"
            description="Repeats daily until your end date."
            selected={repeat === "daily"}
            onClick={() => choose("daily")}
          />
          <OptionCard
            icon={Repeat}
            tone="violet"
            title="Every week"
            description="Repeats weekly until your end date."
            selected={repeat === "weekly"}
            onClick={() => choose("weekly")}
          />
          <OptionCard
            icon={CalendarDays}
            tone="emerald"
            title="Every month"
            description="Repeats monthly until your end date."
            selected={repeat === "monthly"}
            onClick={() => choose("monthly")}
          />
          <OptionCard
            icon={CalendarPlus}
            tone="amber"
            title="Custom dates"
            description="Choose specific dates and times."
            selected={repeat === "custom"}
            onClick={() => choose("custom")}
          />
        </div>
      </QuizQuestion>
    );
  }

  /* ───────────────── 3a. Repeat range (daily/weekly/monthly) ───────────────── */
  if (screen === "range") {
    const startIsSelected = p.scheduleOption === "later";
    const startValue = startIsSelected ? p.selectedDateTime : p.recurringStartDateTime;
    const setStart = (v: string) =>
      startIsSelected ? p.setSelectedDateTime(v) : p.setRecurringStartDateTime(v);

    const apply = () => {
      const { error: err } = p.buildRecurring();
      if (err) {
        toast({
          title: "Recurring schedule incomplete",
          description: err,
          variant: "destructive",
          duration: 2500,
        });
        return;
      }
      p.applyRecurring();
      setScreen("confirm");
    };

    return (
      <QuizQuestion
        {...q}
        title={`When should it start and end? (${REPEAT_LABEL[repeat].toLowerCase()})`}
        hint="Choose the first send and the last day it should repeat."
        onBack={goBack}
        onContinue={apply}
        continueLabel="Apply schedule"
        continueDisabled={!startValue || !p.recurringEndDate}
        size="md"
      >
        <div className="space-y-5">
          <div className="rounded-2xl border border-blue-200 bg-blue-50/60 p-5">
            <FieldLabel required>First send date &amp; time ({p.effectiveTimezone})</FieldLabel>
            <input
              type="datetime-local"
              value={startValue}
              min={p.minDateTime || undefined}
              onClick={openPicker}
              onChange={(e) => {
                const v = e.target.value;
                if (!v) return setStart("");
                if (!validatePast(v, "You cannot schedule a launch in the past.")) return setStart("");
                setStart(v);
              }}
              className={dtInputClass}
            />
            <p className="mt-2 text-xs text-blue-800">
              Please pick a time at least 2 minutes from now.
            </p>
          </div>

          <div className="rounded-2xl border border-amber-200 bg-amber-50/60 p-5">
            <FieldLabel required>Last day to repeat</FieldLabel>
            <input
              type="date"
              value={p.recurringEndDate}
              min={startValue ? startValue.substring(0, 10) : p.minDateTime?.substring(0, 10) || undefined}
              onClick={openPicker}
              onChange={(e) => {
                const v = e.target.value;
                if (!v) return p.setRecurringEndDate("");
                if (new Date(`${v}T23:59:59`) < new Date()) {
                  toast({
                    title: "Invalid Date",
                    description: "You cannot set an end date in the past.",
                    variant: "destructive",
                    duration: 1500,
                  });
                  return p.setRecurringEndDate("");
                }
                if (startValue && new Date(`${v}T23:59:59`) < new Date(startValue)) {
                  toast({
                    title: "Invalid End Date",
                    description: "End date must be on or after the first send date.",
                    variant: "destructive",
                    duration: 2000,
                  });
                  return p.setRecurringEndDate("");
                }
                p.setRecurringEndDate(v);
              }}
              className={dtInputClass}
            />
            <p className="mt-2 text-xs text-amber-800">
              It sends at the same time of day as the first send, up to this date.
            </p>
          </div>
        </div>
      </QuizQuestion>
    );
  }

  /* ───────────────── 3b. Custom dates ───────────────── */
  if (screen === "dates") {
    const add = () => {
      if (!customDraft) return;
      if (!validatePast(customDraft, "You cannot add a past date.")) {
        setCustomDraft("");
        return;
      }
      p.setCustomRecurringDates(p.sortDates([...p.customRecurringDates, customDraft]));
      setCustomDraft("");
    };
    const apply = () => {
      const { error: err } = p.buildRecurring();
      if (err) {
        toast({
          title: "Recurring schedule incomplete",
          description: err,
          variant: "destructive",
          duration: 2500,
        });
        return;
      }
      p.applyRecurring();
      setScreen("confirm");
    };
    return (
      <QuizQuestion
        {...q}
        title="Pick your send dates"
        hint={`Add each date and time you want it to go out (${p.effectiveTimezone}).`}
        onBack={goBack}
        onContinue={apply}
        continueLabel="Apply schedule"
        continueDisabled={p.customRecurringDates.length === 0}
        summary={`${p.customRecurringDates.length} send${p.customRecurringDates.length === 1 ? "" : "s"} selected`}
        size="md"
      >
        <div className="rounded-2xl border border-indigo-200 bg-indigo-50/60 p-5">
          <FieldLabel>Date &amp; time</FieldLabel>
          <div className="flex flex-col gap-3 sm:flex-row">
            <input
              type="datetime-local"
              value={customDraft}
              min={p.minDateTime || undefined}
              onClick={openPicker}
              onChange={(e) => setCustomDraft(e.target.value)}
              className={dtInputClass}
            />
            <Button
              type="button"
              onClick={add}
              disabled={!customDraft}
              className="h-12 shrink-0 rounded-xl px-6"
            >
              <Plus className="mr-1.5 h-4 w-4" /> Add
            </Button>
          </div>
        </div>

        <div className="mt-5 space-y-2">
          {p.customRecurringDates.length === 0 ? (
            <p className="rounded-xl border border-dashed border-slate-300 bg-white p-6 text-center text-sm italic text-slate-500">
              No dates added yet.
            </p>
          ) : (
            p.customRecurringDates.map((d, i) => (
              <div
                key={`${d}-${i}`}
                className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm"
              >
                <span className="flex items-center gap-2 text-sm font-medium text-slate-900">
                  <CalendarClock className="h-4 w-4 text-indigo-500" />
                  {p.formatDisplay(d)}
                </span>
                <button
                  type="button"
                  onClick={() =>
                    p.setCustomRecurringDates(p.customRecurringDates.filter((_, j) => j !== i))
                  }
                  className="rounded-full p-1.5 text-red-500 hover:bg-red-50"
                  aria-label="Remove date"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))
          )}
        </div>
      </QuizQuestion>
    );
  }

  /* ───────────────── 4. Confirm ───────────────── */
  const recurring = p.isRecurringCampaign;
  const firstSend =
    p.scheduleOption === "later"
      ? p.selectedDateTime
      : recurring
        ? p.recurringStartDateTime
        : "";

  const rows: { label: string; value: React.ReactNode; to: Screen }[] = [
    {
      label: "Send",
      value:
        p.scheduleOption === "now" && !recurring
          ? "Immediately (within about a minute)"
          : firstSend
            ? p.formatDisplay(firstSend)
            : recurring && repeat === "custom"
              ? "On your custom dates"
              : "Scheduled",
      to: "when",
    },
    {
      label: "Repeats",
      value: recurring ? REPEAT_LABEL[p.recurrenceType] : "Just once",
      to: "repeat",
    },
  ];
  if (recurring && p.recurrenceType !== "custom" && p.recurringEndDate) {
    rows.push({ label: "Ends", value: p.recurringEndDate, to: "range" });
  }
  if (recurring && p.recurringCount) {
    rows.push({
      label: "Total sends",
      value: `${p.recurringCount} send${p.recurringCount === 1 ? "" : "s"}`,
      to: "repeat",
    });
  }

  return (
    <QuizQuestion
      {...q}
      title="Ready to confirm your schedule?"
      hint="Check the details, accept the confirmation, and continue to payment or launch."
      onBack={goBack}
      onContinue={p.onContinue}
      continueDisabled={!p.canContinue}
      continueLoading={p.continueLoading}
      size="md"
    >
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <dl className="divide-y divide-slate-100">
          {rows.map((r) => (
            <div key={r.label} className="flex items-start justify-between gap-4 px-5 py-3">
              <div className="min-w-0">
                <dt className="text-xs font-medium uppercase tracking-wide text-slate-400">
                  {r.label}
                </dt>
                <dd className="break-words text-sm font-semibold text-slate-900">{r.value}</dd>
              </div>
              <button
                type="button"
                onClick={() => setScreen(r.to === "range" && repeat === "custom" ? "dates" : r.to)}
                className="shrink-0 text-sm font-medium text-blue-600 hover:underline"
              >
                Change
              </button>
            </div>
          ))}
          {recurring && p.recurrenceType === "custom" && p.customRecurringDates.length > 0 && (
            <div className="px-5 py-3">
              <dt className="mb-1.5 text-xs font-medium uppercase tracking-wide text-slate-400">
                Selected send dates
              </dt>
              <dd className="space-y-1">
                {p.customRecurringDates.map((d, i) => (
                  <p key={i} className="flex items-center gap-2 text-sm text-slate-800">
                    <CalendarClock className="h-3.5 w-3.5 text-indigo-500" />
                    {p.formatDisplay(d)}
                  </p>
                ))}
              </dd>
            </div>
          )}
        </dl>
        {recurring && (
          <div className="border-t border-slate-100 bg-slate-50 px-5 py-3">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                p.removeRecurring();
                setRepeat("none");
              }}
              className="h-auto px-2 py-1 text-xs text-red-600 hover:bg-red-50"
            >
              <Trash2 className="mr-1 h-3.5 w-3.5" /> Remove recurring schedule
            </Button>
          </div>
        )}
      </div>

      <div className="mt-5 flex items-start gap-3 rounded-2xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
        <Info className="mt-0.5 h-5 w-5 shrink-0 text-blue-600" />
        <p>
          Your payment summary (extra launches and hubs, if any) is shown on the next step. You
          must accept the confirmation below to continue with “Send now” or a recurring schedule.
        </p>
      </div>

      <label
        htmlFor="quiz-accept-terms"
        className="mt-5 flex cursor-pointer items-start gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
      >
        <input
          id="quiz-accept-terms"
          type="checkbox"
          checked={p.acceptedTerms}
          onChange={(e) => p.setAcceptedTerms(e.target.checked)}
          className="mt-0.5 h-5 w-5 rounded"
        />
        <span className="text-sm text-slate-700">
          I accept the preview and confirm all details are accurate. I understand times are shown
          in my timezone and saved in UTC.
        </span>
      </label>
    </QuizQuestion>
  );
};
